import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import Seo from "../../components/Seo";

const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm";

export default function AdminTeam() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "sales" });
  const { data } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/admin/team")).data });
  const isSuper = user?.role === "super_admin";

  const save = async () => {
    if (!f.name || !f.email || !f.password) return toast.error("All fields required");
    try { await api.post("/admin/team", f); toast.success("Member added"); setOpen(false); setF({ name: "", email: "", password: "", role: "sales" }); qc.invalidateQueries({ queryKey: ["team"] }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (u) => { if (!window.confirm(`Remove ${u.name}?`)) return; try { await api.delete(`/admin/team/${u.id}`); toast.success("Removed"); qc.invalidateQueries({ queryKey: ["team"] }); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div>
      <Seo title="Team | Admin" />
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Team & Roles</h1><p className="text-slate-500">Manage admins and sales team members.</p></div>
        {isSuper && <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-indigo-900 hover:bg-indigo-800" data-testid="add-team-btn"><Plus className="w-4 h-4 mr-2" /> Add Member</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={inp} data-testid="team-name" />
              <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={inp} data-testid="team-email" />
              <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" className={inp} data-testid="team-password" />
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="sales">Sales Team</SelectItem></SelectContent></Select>
            </div>
            <DialogFooter><Button onClick={save} className="bg-indigo-900 hover:bg-indigo-800" data-testid="team-save">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 mt-6 divide-y divide-slate-100">
        {(data?.items || []).map((u) => (
          <div key={u.id} className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-800 flex items-center justify-center"><UserCog className="w-5 h-5" /></div>
              <div><div className="font-medium text-slate-900">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs bg-slate-100 px-2.5 py-1 rounded-full capitalize text-slate-600">{u.role.replace("_", " ")}</span>
              {isSuper && u.role !== "super_admin" && <button onClick={() => del(u)} className="p-1.5 hover:bg-rose-50 rounded-lg"><Trash2 className="w-4 h-4 text-rose-500" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
